import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Slider,
  Paper,
  Button
} from '@mui/material';
import { SketchPicker, ColorResult } from 'react-color';
import { ThemeSettings } from './SiteCustomizationManager';

interface ThemeEditorProps {
  settings: ThemeSettings;
  onChange: (settings: ThemeSettings) => void;
  onPreview: () => void;
}

const WEB_SAFE_FONTS = [
  'Arial',
  'Helvetica',
  'Times New Roman',
  'Georgia',
  'Courier New',
  'Verdana',
  'Trebuchet MS',
  'Impact',
  'Comic Sans MS',
  'Palatino'
];

const GOOGLE_FONTS = [
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Oswald',
  'Raleway',
  'PT Sans',
  'Merriweather',
  'Nunito',
  'Playfair Display',
  'Poppins',
  'Ubuntu',
  'Mukta',
  'Rubik',
  'Work Sans'
];

const ALL_FONTS = [...WEB_SAFE_FONTS, ...GOOGLE_FONTS].sort();

export const ThemeEditor: React.FC<ThemeEditorProps> = ({ settings, onChange, onPreview }) => {
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);

  const handleColorChange = (colorKey: keyof ThemeSettings['colors'], color: ColorResult) => {
    onChange({
      ...settings,
      colors: {
        ...settings.colors,
        [colorKey]: color.hex
      }
    });
  };

  const handleFontChange = (fontKey: 'heading' | 'body', value: string) => {
    onChange({
      ...settings,
      fonts: {
        ...settings.fonts,
        [fontKey]: value
      }
    });
  };

  const handleFontSizeChange = (sizeKey: 'headingSize' | 'bodySize', value: number) => {
    onChange({
      ...settings,
      fonts: {
        ...settings.fonts,
        [sizeKey]: value
      }
    });
  };

  const ColorPickerButton: React.FC<{
    label: string;
    colorKey: keyof ThemeSettings['colors'];
    color: string;
  }> = ({ label, colorKey, color }) => (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box
          onClick={() => setShowColorPicker(colorKey)}
          sx={{
            width: 60,
            height: 40,
            backgroundColor: color,
            border: '2px solid #ccc',
            borderRadius: 1,
            cursor: 'pointer',
            '&:hover': {
              border: '2px solid #999'
            }
          }}
        />
        <TextField
          value={color}
          onChange={(e) => handleColorChange(colorKey, { hex: e.target.value } as ColorResult)}
          size="small"
          sx={{ width: 120 }}
        />
      </Box>
      {showColorPicker === colorKey && (
        <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              left: 0
            }}
            onClick={() => setShowColorPicker(null)}
          />
          <SketchPicker
            color={color}
            onChange={(color) => handleColorChange(colorKey, color)}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Theme Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize colors, fonts, and typography to match your event's branding.
      </Typography>

      <Grid container spacing={4}>
        {/* Color Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Colors
            </Typography>
            
            <ColorPickerButton
              label="Primary Color"
              colorKey="primary"
              color={settings.colors.primary}
            />
            
            <ColorPickerButton
              label="Secondary Color"
              colorKey="secondary"
              color={settings.colors.secondary}
            />
            
            <ColorPickerButton
              label="Accent Color"
              colorKey="accent"
              color={settings.colors.accent}
            />
            
            <ColorPickerButton
              label="Background Color"
              colorKey="background"
              color={settings.colors.background}
            />
            
            <ColorPickerButton
              label="Text Color"
              colorKey="text"
              color={settings.colors.text}
            />
          </Paper>
        </Grid>

        {/* Gradient Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Background Gradient
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Add a gradient background to your pages (like the login page)
            </Typography>
            
            <Box sx={{ mb: 3 }}>
              <FormControl fullWidth>
                <InputLabel>Gradient</InputLabel>
                <Select
                  value={settings.gradient?.enabled ? 'enabled' : 'disabled'}
                  label="Gradient"
                  onChange={(e) => onChange({
                    ...settings,
                    gradient: {
                      ...settings.gradient,
                      enabled: e.target.value === 'enabled',
                      startColor: settings.gradient?.startColor || '#667eea',
                      endColor: settings.gradient?.endColor || '#764ba2',
                      angle: settings.gradient?.angle || 135
                    }
                  })}
                >
                  <MenuItem value="disabled">Disabled</MenuItem>
                  <MenuItem value="enabled">Enabled</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {settings.gradient?.enabled && (
              <>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Start Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      onClick={() => setShowColorPicker('gradientStart')}
                      sx={{
                        width: 60,
                        height: 40,
                        backgroundColor: settings.gradient?.startColor || '#667eea',
                        border: '2px solid #ccc',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          border: '2px solid #999'
                        }
                      }}
                    />
                    <TextField
                      value={settings.gradient?.startColor || '#667eea'}
                      onChange={(e) => onChange({
                        ...settings,
                        gradient: {
                          ...settings.gradient!,
                          startColor: e.target.value
                        }
                      })}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {showColorPicker === 'gradientStart' && (
                    <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
                      <Box
                        sx={{
                          position: 'fixed',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0
                        }}
                        onClick={() => setShowColorPicker(null)}
                      />
                      <SketchPicker
                        color={settings.gradient?.startColor || '#667eea'}
                        onChange={(color) => onChange({
                          ...settings,
                          gradient: {
                            ...settings.gradient!,
                            startColor: color.hex
                          }
                        })}
                      />
                    </Box>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    End Color
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                      onClick={() => setShowColorPicker('gradientEnd')}
                      sx={{
                        width: 60,
                        height: 40,
                        backgroundColor: settings.gradient?.endColor || '#764ba2',
                        border: '2px solid #ccc',
                        borderRadius: 1,
                        cursor: 'pointer',
                        '&:hover': {
                          border: '2px solid #999'
                        }
                      }}
                    />
                    <TextField
                      value={settings.gradient?.endColor || '#764ba2'}
                      onChange={(e) => onChange({
                        ...settings,
                        gradient: {
                          ...settings.gradient!,
                          endColor: e.target.value
                        }
                      })}
                      size="small"
                      sx={{ width: 120 }}
                    />
                  </Box>
                  {showColorPicker === 'gradientEnd' && (
                    <Box sx={{ position: 'absolute', zIndex: 2, mt: 1 }}>
                      <Box
                        sx={{
                          position: 'fixed',
                          top: 0,
                          right: 0,
                          bottom: 0,
                          left: 0
                        }}
                        onClick={() => setShowColorPicker(null)}
                      />
                      <SketchPicker
                        color={settings.gradient?.endColor || '#764ba2'}
                        onChange={(color) => onChange({
                          ...settings,
                          gradient: {
                            ...settings.gradient!,
                            endColor: color.hex
                          }
                        })}
                      />
                    </Box>
                  )}
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Gradient Angle: {settings.gradient?.angle || 135}°
                  </Typography>
                  <Slider
                    value={settings.gradient?.angle || 135}
                    onChange={(_, value) => onChange({
                      ...settings,
                      gradient: {
                        ...settings.gradient!,
                        angle: value as number
                      }
                    })}
                    min={0}
                    max={360}
                    step={15}
                    marks={[
                      { value: 0, label: '0°' },
                      { value: 90, label: '90°' },
                      { value: 180, label: '180°' },
                      { value: 270, label: '270°' },
                      { value: 360, label: '360°' }
                    ]}
                  />
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Preview
                  </Typography>
                  <Box
                    sx={{
                      width: '100%',
                      height: 80,
                      borderRadius: 1,
                      background: `linear-gradient(${settings.gradient?.angle || 135}deg, ${settings.gradient?.startColor || '#667eea'} 0%, ${settings.gradient?.endColor || '#764ba2'} 100%)`,
                      border: '1px solid #ccc'
                    }}
                  />
                </Box>
              </>
            )}
          </Paper>
        </Grid>

        {/* Font Settings */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Typography
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Heading Font</InputLabel>
              <Select
                value={settings.fonts.heading}
                label="Heading Font"
                onChange={(e) => handleFontChange('heading', e.target.value)}
              >
                {ALL_FONTS.map(font => (
                  <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Heading Size: {settings.fonts.headingSize}px
              </Typography>
              <Slider
                value={settings.fonts.headingSize}
                onChange={(_, value) => handleFontSizeChange('headingSize', value as number)}
                min={16}
                max={48}
                step={1}
                marks={[
                  { value: 16, label: '16px' },
                  { value: 32, label: '32px' },
                  { value: 48, label: '48px' }
                ]}
              />
            </Box>

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Body Font</InputLabel>
              <Select
                value={settings.fonts.body}
                label="Body Font"
                onChange={(e) => handleFontChange('body', e.target.value)}
              >
                {ALL_FONTS.map(font => (
                  <MenuItem key={font} value={font} style={{ fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Body Size: {settings.fonts.bodySize}px
              </Typography>
              <Slider
                value={settings.fonts.bodySize}
                onChange={(_, value) => handleFontSizeChange('bodySize', value as number)}
                min={12}
                max={24}
                step={1}
                marks={[
                  { value: 12, label: '12px' },
                  { value: 18, label: '18px' },
                  { value: 24, label: '24px' }
                ]}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Preview Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Live Preview
            </Typography>
            <Box
              sx={{
                p: 3,
                backgroundColor: settings.colors.background,
                color: settings.colors.text,
                borderRadius: 1,
                border: '1px solid #ccc'
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontFamily: settings.fonts.heading,
                  fontSize: `${settings.fonts.headingSize}px`,
                  color: settings.colors.primary,
                  mb: 2
                }}
              >
                Sample Heading
              </Typography>
              <Typography
                sx={{
                  fontFamily: settings.fonts.body,
                  fontSize: `${settings.fonts.bodySize}px`,
                  mb: 2
                }}
              >
                This is sample body text to show how your content will look with the selected fonts and colors.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: settings.colors.primary,
                  color: '#fff',
                  mr: 2,
                  '&:hover': {
                    backgroundColor: settings.colors.primary,
                    opacity: 0.9
                  }
                }}
              >
                Primary Button
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: settings.colors.secondary,
                  color: '#fff',
                  mr: 2,
                  '&:hover': {
                    backgroundColor: settings.colors.secondary,
                    opacity: 0.9
                  }
                }}
              >
                Secondary Button
              </Button>
              <Button
                variant="contained"
                sx={{
                  backgroundColor: settings.colors.accent,
                  color: '#fff',
                  '&:hover': {
                    backgroundColor: settings.colors.accent,
                    opacity: 0.9
                  }
                }}
              >
                Accent Button
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
